import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import JoditEditor from "jodit-react";
import {
  FiUpload,
  FiClock,
  FiFileText,
  FiLayers,
  FiCheckCircle,
  FiChevronLeft,
  FiAlertCircle,
  FiInfo,
} from "react-icons/fi";
import SectionAndQuestionManager from "./Questions";
import { ToastContainer } from "react-toastify";

export default function ExamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [icon, setIcon] = useState("");
  const [imageUploadIcon, setImageUploadIcon] = useState(false);
  const [totalSections, setTotalSections] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sections, setSections] = useState([]);

  const [showGuidelineModal, setShowGuidelineModal] = useState(false);
  const [guidelines, setGuidelines] = useState("");
  const [savingGuideline, setSavingGuideline] = useState(false);

  const getExamDetails = async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/admin/getExamById", {
        examId: id,
      });
      if (res?.error) return showErrorToast(res.message);
      setExam(res.data);
      setTotalSections(res?.data?.totalSections ?? 0);
      setIcon(res.data?.icon || "");
    } catch {
      showErrorToast("Failed to load exam details");
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    const res = await makeRequestAuth("post", "/admin/getExamQuestions", {
      sourceId: id,
      pageNo: 1,
      size: 100,
    });
    if (!res?.error) setSections(res.data?.list || []);
  };

  const isExamComplete = () => {
    if (!exam) return false;
    if (sections.length !== exam.totalSections) return false;
    for (const sec of sections) {
      if (sec.questions.length !== sec.totalQuestions) return false;
    }
    return true;
  };

  const handleIconChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      let res = await uploadFiles({
        files: [file],
      });
      setImageUploadIcon(true);
      setIcon(res?.data?.imageUrl);
    } catch (error) {
      showErrorToast(error.message || "Faild To Upload Image");
    }
  };

  const uploadIcon = async () => {
    if (!icon) return;
    setUploading(true);
    let formData = {
      examId: id,
      icon,
    };
    try {
      const res = await makeRequestAuth(
        "post",
        "/admin/updateExam",
        formData,
        true,
      );
      if (res?.error) throw new Error(res.message);
      showSuccessToast("Icon updated successfully");
      setImageUploadIcon(false);
      getExamDetails();
    } catch (err) {
      showErrorToast(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateExamStatus = async (status) => {
    try {
      const res = await makeRequestAuth("post", "/admin/examStatusTongel", {
        id,
        status,
      });
      if (res?.error) return showErrorToast(res.message);
      showSuccessToast(`Exam marked as ${status}`);
      getExamDetails();
    } catch (err) {
      showErrorToast(err.message || "Failed to update status");
    }
  };

  useEffect(() => {
    getExamDetails();
    fetchSections();
  }, []);

  const saveGuidelines = async () => {
    if (!guidelines || guidelines.trim().length < 20) {
      return showErrorToast("Guidelines thoda detail me likho yaar");
    }

    try {
      setSavingGuideline(true);

      const res = await makeRequestAuth(
        "post",
        "/admin/updateExam",
        {
          examId: id,
          guildelines: guidelines,
        },
        true,
      );

      if (res?.error) throw new Error(res.message);

      showSuccessToast("Guidelines saved successfully");
      setShowGuidelineModal(false);
      getExamDetails();
    } catch (err) {
      showErrorToast(err.message || "Failed to save guidelines");
    } finally {
      setSavingGuideline(false);
    }
  };

  useEffect(() => {
    if (exam?.guildelines) {
      setGuidelines(exam.guildelines);
    }
  }, [exam]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-500 font-medium">Fetching Exam Data...</p>
        </div>
      </div>
    );

  if (!exam)
    return <div className="p-10 text-center text-red-500">Exam not found.</div>;

  const complete = isExamComplete();

  return (
    <div className="p-4 antialiased text-slate-900">
      <ToastContainer />
      <div className="max-w-6xl mx-auto">
        {/* BACK BUTTON */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-blue-600 mb-6 transition-colors font-medium group"
        >
          <FiChevronLeft className="group-hover:-translate-x-1 transition-transform" />{" "}
          Back to Dashboard
        </button>

        {/* HERO HEADER */}
        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden mb-8">
          <div className="h-40 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 relative">
            <div className="absolute top-4 right-6">
              <StatusBadge status={exam.status} />
            </div>
          </div>

          <div className="px-8 pb-8">
            <div className="flex flex-col md:flex-row items-start md:items-end -mt-16 gap-6 relative z-10">
              {/* IMAGE BOX */}
              <div className="relative group shrink-0">
                <img
                  src={icon || "https://via.placeholder.com/150"}
                  alt="Exam Icon"
                  className="w-36 h-36 rounded-[2rem] object-cover border-[6px] border-white shadow-xl bg-white"
                />
                <label className="absolute inset-0 flex items-center justify-center bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-all rounded-[2rem] cursor-pointer backdrop-blur-[2px]">
                  <FiUpload className="text-white text-3xl" />
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleIconChange}
                  />
                </label>
              </div>

              {/* TITLE AREA */}
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-3xl font-extrabold tracking-tight text-slate-800">
                    {exam.testName}
                  </h1>
                  {imageUploadIcon && (
                    <button
                      onClick={uploadIcon}
                      disabled={uploading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-full text-xs font-bold transition-all disabled:opacity-50 animate-bounce"
                    >
                      {uploading ? "Saving..." : "Click to Save New Icon"}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-4 text-slate-500 font-medium">
                  <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-lg text-xs font-bold text-slate-600 uppercase tracking-wider">
                    {exam.testYear}
                  </span>
                  <span>•</span>
                  <span className="capitalize">{exam.language}</span>
                  <span>•</span>
                  <span className="text-blue-600">
                    ID: {id.substring(0, 8)}
                  </span>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex flex-row md:flex-col gap-3 w-full md:w-auto">
                <button
                  onClick={() => updateExamStatus("active")}
                  disabled={!complete || exam.status === "active"}
                  className={`flex-1 md:w-48 py-3 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2
                                    ${
                                      !complete || exam.status === "active"
                                        ? "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                                        : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-200"
                                    }`}
                >
                  <FiCheckCircle />{" "}
                  {exam.status === "active" ? "Exam Active" : "Activate Exam"}
                </button>
                {exam.status !== "inactive" && (
                  <button
                    onClick={() => updateExamStatus("inactive")}
                    className="flex-1 md:w-48 py-3 rounded-2xl font-bold text-sm bg-white text-rose-600 border border-rose-100 hover:bg-rose-50 transition-all shadow-sm"
                  >
                    Deactivate Exam
                  </button>
                )}

                <button
                  onClick={() => setShowGuidelineModal(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold"
                >
                  {exam?.guildelines ? "View Guidelines" : "Add Guidelines"}
                </button>
              </div>
            </div>

            {!complete && exam.status !== "active" && (
              <div className="mt-6 flex items-center gap-2 p-4 bg-amber-50 rounded-2xl border border-amber-100 text-amber-700 text-sm font-medium">
                <FiAlertCircle className="shrink-0" size={18} />
                Verification required: All sections and questions must be added
                before activation.
              </div>
            )}
          </div>
        </div>

        {/* STATS & METADATA GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={<FiFileText />}
              label="Questions"
              value={exam.totalQuestions}
              theme="amber"
            />
            <StatCard
              icon={<FiLayers />}
              label="Sections"
              value={exam.totalSections}
              theme="indigo"
            />
            <StatCard
              icon={<FiClock />}
              label="Duration"
              value={`${exam.durationMinutes}m`}
              theme="emerald"
            />
            <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm">
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2">
                Difficulty
              </p>
              <span
                className={`text-sm font-extrabold px-3 py-1 rounded-full uppercase ${
                  exam.difficulty === "hard"
                    ? "bg-rose-50 text-rose-600"
                    : exam.difficulty === "medium"
                      ? "bg-blue-50 text-blue-600"
                      : "bg-emerald-50 text-emerald-600"
                }`}
              >
                {exam.difficulty}
              </span>
            </div>
          </div>

          <div className="bg-slate-900 text-white p-6 rounded-[2rem] shadow-xl relative overflow-hidden group">
            <div className="relative z-10">
              <h3 className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
                <FiInfo /> Meta Details
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-xs">Code</span>
                  <span className="font-mono text-sm font-bold tracking-tighter">
                    {exam.examCode || "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/50 text-xs">Popular</span>
                  <span className="text-sm font-bold">
                    {exam.isPopular ? "Yes ✅" : "No"}
                  </span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white/5 rounded-full blur-2xl group-hover:scale-150 transition-transform" />
          </div>
        </div>

        {/* CONTENT MANAGER */}
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-2 md:p-4">
          <SectionAndQuestionManager
            examId={id}
            totalSections={totalSections}
          />
        </div>
      </div>

      {showGuidelineModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6">
          <div className="bg-white w-full max-w-3xl h-[90vh] sm:h-auto sm:max-h-[85vh] rounded-2xl sm:rounded-3xl shadow-xl flex flex-col">
            {/* HEADER */}
            <div className="px-4 sm:px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-base sm:text-xl font-extrabold">
                {exam?.guidelines ? "Exam Guidelines" : "Add Exam Guidelines"}
              </h2>

              <button
                onClick={() => setShowGuidelineModal(false)}
                className="text-gray-500 hover:text-red-500 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4">
              <JoditEditor
                value={guidelines}
                onChange={(newContent) => setGuidelines(newContent)}
              />
            </div>

            {/* FOOTER */}
            <div className="px-4 sm:px-6 py-4 border-t flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => setShowGuidelineModal(false)}
                className="w-full sm:w-auto px-4 py-2 rounded-xl border font-semibold"
              >
                Cancel
              </button>

              <button
                onClick={saveGuidelines}
                disabled={savingGuideline}
                className="w-full sm:w-auto px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50"
              >
                {savingGuideline ? "Saving..." : "Save Guidelines"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== ENHANCED UI COMPONENTS ===== */

function StatCard({ icon, label, value, theme }) {
  const styles = {
    amber: "bg-amber-50 text-amber-600",
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
  };

  return (
    <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
      <div
        className={`w-10 h-10 rounded-2xl ${styles[theme]} flex items-center justify-center text-xl mb-3`}
      >
        {icon}
      </div>
      <div>
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">
          {label}
        </p>
        <p className="text-xl font-black text-slate-800">{value || "0"}</p>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500 text-white",
    inactive: "bg-rose-500 text-white",
    draft: "bg-amber-400 text-slate-900",
  };

  return (
    <span
      className={`px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-black/10 backdrop-blur-md ${map[status] || map.draft}`}
    >
      {status}
    </span>
  );
}
